<template>
  <div>
    <div class="py-3 mb-3 border-radius-lg pe-1" :class="`bg-gradient-${color}`">
      <div class="position-relative">
        <h6 class="position-absolute top-0 start-1 ms-2 mt-0 text-white text-xs" style="z-index: 1;">
          최근 9일
        </h6>
        <div class="pt-3 chart">
          <canvas ref="canvas" class="chart-canvas" height="170"></canvas>
        </div>
      </div>
    </div>

    <!-- <h6 class="mb-0 ms-2">{{ title }}</h6>
    <p class="text-sm ms-2 mb-0" v-html="computedDescription" /> -->

    <div class="container border-radius-lg">
      <div class="row">
        <div
          v-for="({ label, progress: { content }, icon: { color: colour, component } }, idx) in items"
          :key="idx"
          class="py-3 col-3 ps-0"
        >
          <div class="mb-2 d-flex align-items-center justify-content-center">
            <div
              class="text-center shadow icon icon-shape icon-sm border-radius-sm me-3 d-flex align-items-center justify-content-center"
              :class="`bg-gradient-${colour}`"
              style="width:40px; height:40px;"
            >
              <i class="text-lg" :class="component" style="top: 2px;"></i>
            </div>
            <div class="stat-text">
              <p class="stat-label mb-0">{{ label }}</p>
              <h4 class="stat-value mb-0">{{ content }}</h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { markRaw, toRaw } from "vue";

export default {
  name: "ReportsBarChart",
  props: {
    color: { type: String, default: "dark" },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    // { todayCount: Number, dailyCounts: { 'YYYY-MM-DD': Number, ... } }
    stats: { type: Object, required: true },
    items: { type: Array, default: () => [] },
  },
  data() {
    return { _chart: null, _retryTimer: null };
  },
  computed: {
    sortedDates() {
      const dc = this.stats?.dailyCounts || {};
      return Object.keys(dc).sort();
    },
    labels() {
      return this.sortedDates.map((d) => d.slice(5)); // 'MM-DD'
    },
    counts() {
      const dc = this.stats?.dailyCounts || {};
      return this.sortedDates.map((d) => {
        const v = dc[d] ?? 0;
        return v === 0 ? 0.2 : v; // 0면 아주 작은 값으로 표시
      });
    },
    computedDescription() {
      if (this.description) return this.description;
      const today = this.stats?.todayCount ?? 0;
      return `<strong>TODAY ${today}</strong>`;
    },
  },
  mounted() {
    this.queueRender();
  },
  // keep-alive 대비
  activated() {
    this.queueRender(); // 복귀 시 다시 그리기(컨테이너가 보이는 타이밍에)
  },
  deactivated() {
    this.destroyChart(); // 떠날 때 정리
  },
  watch: {
    stats: {
      deep: true,
      handler() {
        // 차트가 있으면 업데이트, 없으면 안전하게 렌더 큐
        if (this._chart) this.updateChart();
        else this.queueRender();
      },
    },
    color() {
      // 색상만 바뀌어도 다시 그림
      if (this._chart) this.updateChart();
      else this.queueRender();
    },
  },
  methods: {
    queueRender() {
      // DOM 준비 + width 준비까지 대기
      this.$nextTick(() => {
        const canvas = this.$refs.canvas;
        const parent = canvas?.parentElement;
        const w = parent?.clientWidth || 0;

        if (!canvas || w === 0) {
          clearTimeout(this._retryTimer);
          this._retryTimer = setTimeout(() => this.queueRender(), 50);
          return;
        }
        this.renderChart();
      });
    },
    renderChart() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      this.destroyChart(); // 기존 인스턴스 정리

      const ctx = canvas.getContext("2d");
      if (!ctx) return; // 방어

      const labels = [...this.labels];
      const data = [...this.counts];

      const cfg = {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: "이벤트 수",
            tension: 0.4,
            borderWidth: 0,
            borderRadius: 4,
            borderSkipped: false,
            backgroundColor: "#fff",
            data,
            maxBarThickness: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 700, easing: 'easeOutQuart' },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const original = this.stats?.dailyCounts?.[this.sortedDates[ctx.dataIndex]] ?? 0;
                  return `${original}`;
                }
              }
            }
          },
          interaction: { intersect: false, mode: "index" },
          events: ["mousemove","mouseout","click","touchstart","touchmove"],
          scales: {
            y: {
              grid: { drawBorder: false, display: false, drawOnChartArea: false, drawTicks: false },
              ticks: {
                suggestedMin: 0,
                beginAtZero: true,
                padding: 15,
                font: { size: 14, family: "Open Sans", style: "normal", lineHeight: 2 },
                color: "#fff",
                precision: 0,
              },
            },
            x: {
              grid: { drawBorder: false, display: false, drawOnChartArea: false, drawTicks: false },
              ticks: { display: false },
            },
          },
        },
      };

      this._chart = markRaw(new Chart(ctx, cfg));
    },
    updateChart() {
      if (!this._chart) return this.queueRender();
      const ch = toRaw(this._chart);
      ch.data.labels = [...this.labels];
      ch.data.datasets[0].data = [...this.counts];
      ch.options.animation = { duration: 700, easing: 'easeOutQuart' };
      ch.update(); 
    },
    destroyChart() {
      if (this._chart) { this._chart.destroy(); this._chart = null; }
      clearTimeout(this._retryTimer);
    },
  },
  beforeUnmount() {
    this.destroyChart();
  },
};
</script>


<style scoped>
.stat-text{
  display:flex;
  flex-direction:column;
  align-items:flex-start; /* 아이콘 기준 왼쪽 정렬 */
  gap: 2px;               /* 라벨-숫자 사이 간격 */
  line-height: 1;         /* 줄간격 통일 */
}

.stat-label{
  font-size: 0.875rem;    /* text-sm */
  font-weight: 600;       /* font-weight-bold 정도 */
  color: #6b7280;         /* 살짝 연한 회색 */
}

.stat-value{
  font-size: 1.125rem;    /* text-lg */
  font-weight: 800;       /* font-weight-bolder */
  line-height: 1.1;
}

</style>